import { connect } from 'react-redux';
import { getLandingStack } from 'features/landing/actions';
import {
  setTopNavColorLight,
  setTopNavColorDark
} from 'features/navigation/header/actions';
import { onEntryChange } from 'utilities/contentStack';

import React, { Fragment, useEffect } from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { brand, darkTheme, lightTheme, palette } from 'theme';
import loadable from '@loadable/component';
import PrerenderSafeLazyLoad from 'components/PrerenderSafeLazyLoad';

import { MuiThemeProvider } from '@material-ui/core/styles';

import Container from 'components/Container';
import MetaTags from 'components/MetaTags';
import SwiftypeTags from 'components/SwiftypeTags';
import { ResponsiveBackgroundImg } from 'components/ResponsiveImg';
import { SectionWrapper } from 'components/Section';
import LandingHeader from './elements/LandingHeader';
import SectionNavigation from 'src/components/SectionNavigation';

const BackgroundVideo = loadable(() => import('components/BackgroundVideo'));

const ResponsiveImg = loadable(() => import('components/ResponsiveImg'));
const ThemedLottie = loadable(() => import('components/ThemedLottie'));

const LegacyLandingContent = loadable(() =>
  import('./elements/LegacyLandingContent')
);
const LandingContent = loadable(() => import('./elements/LandingContent'));
const SimpleFooterCTA = loadable(() => import('components/SimpleFooterCTA'));

const LandingWrapper = styled(SectionWrapper)`
  background-color: ${props => props.backgroundcolor || palette.grey[800]};
  margin-top: -64px;
  padding-bottom: 10vh;
  padding-top: 84px;
  width: 100%;
  position: relative;
  z-index: 1;
`;

const FooterImg = styled(ResponsiveImg)`
  position: relative;
  z-index: -1;
  img {
    margin: -5vh auto 0;
    width: 75vw;
  }
`;

const LottieWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: -1;
  width: 100vw;
  height: 50vh;
  @media (max-width: 640px) {
    width: 200vw;
    left: -100vw;
    opacity: 0.5;
  }
`;

const LandingBackgroundImg = styled(ResponsiveBackgroundImg)`
  bottom: auto;
  z-index: -1;
`;

const BrandGradientOverlay = styled.div`
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  position: absolute;
  z-index: 0;
  mix-blend-mode: multiply;
  background-image: linear-gradient(
      to top,
      rgba(255, 255, 255, 1) 30%,
      rgba(255, 255, 255, 0) 90%
    ),
    linear-gradient(${props => props.theme?.brand?.gradient?.primary});
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

export const Landing = ({
  setTopNavColorDark,
  setTopNavColorLight,
  getLandingStack,
  stack,
  match
}) => {
  const brandColor =
    brand?.[stack.page_options?.brand_color?.color] || brand.main;
  const brandContrastTheme =
    brandColor?.contrastTheme === 'dark' ? darkTheme : lightTheme;

  const selectedTheme =
    stack.page_options.theme === 'dark'
      ? darkTheme
      : stack.page_options.theme === 'gradient'
      ? brandContrastTheme
      : lightTheme;

  const brandTheme = { ...selectedTheme, brand: brandColor };
  const darkBrandTheme = { ...darkTheme, brand: brandColor };

  const headerTheme =
    stack.header?.style === 'brand_gradient'
      ? { ...brandContrastTheme, brand: brandColor }
      : selectedTheme;

  const [ref, inView] = useInView({
    initialInView: true
  });

  useEffect(() => {
    getLandingStack(match.url);
  }, [getLandingStack, match.url]);

  useEffect(() => {
    onEntryChange(() => {
      getLandingStack(match.url);
    });
  }, [getLandingStack, match.url]);

  useEffect(() => {
    if (
      brandTheme?.palette?.type === 'dark' ||
      (stack.header?.style === 'brand_gradient' &&
        brandContrastTheme?.palette?.type === 'dark')
    ) {
      setTopNavColorLight();
    } else {
      setTopNavColorDark();
    }
    return () => setTopNavColorDark();
  }, [
    stack,
    setTopNavColorDark,
    setTopNavColorLight,
    brandTheme,
    brandContrastTheme
  ]);

  return (
    <Fragment>
      <MetaTags stack={stack} />
      <SwiftypeTags
        stack={stack}
        title={`${stack.header.heading || stack.title}: ${
          stack.header.subheading
        }`}
      />
      <MuiThemeProvider theme={brandTheme}>
        <LandingWrapper
          backgroundcolor={
            stack.page_options.theme === 'dark'
              ? darkTheme.palette.grey[800]
              : 'transparent'
          }
          animatedBackground={stack.page_options.theme === 'gradient'}
          theme={brandTheme}
        >
          <MuiThemeProvider theme={headerTheme}>
            {stack.page_options?.theme === 'gradient' && (
              <BrandGradientOverlay theme={headerTheme} />
            )}

            {(stack.header.background_type === 'video' ||
              (!stack.header.background_type &&
                stack.header.background_video?.video_files?.length > 0)) && (
              <BackgroundVideo
                videos={stack.header.background_video?.video_files}
                posterurl={stack.header.background_video?.poster?.url}
              />
            )}

            {stack.header.background_type === 'lottie' && (
              <LottieWrapper>
                <ThemedLottie
                  options={{
                    loop: false,
                    animationData: JSON.parse(
                      stack.header?.background_lottie_animation?.animation_data
                    )
                  }}
                />
              </LottieWrapper>
            )}
            {stack.page_options?.use_section_layout &&
              stack.page_options?.show_section_nav_bar && (
                <SectionNavigation
                  hide={inView}
                  title={stack.title}
                  sections={stack.section_content.sections}
                  show_toc={stack.table_of_contents?.show}
                  theme={darkBrandTheme}
                />
              )}
            <Container ref={ref}>
              {(stack.header.background_type === 'image' ||
                (!stack.header.background_type &&
                  stack.header.background_video?.video_files?.length ===
                    0)) && (
                <LandingBackgroundImg
                  src={stack.header.background_image?.url}
                  defaultWidth={1200}
                  backgroundcolor={
                    stack.page_options.theme === 'light'
                      ? 'white'
                      : darkTheme.palette.grey[800]
                  }
                  opacity={stack.header?.background_image_opacity || '0.25'}
                />
              )}
              {!stack.header.hide && (
                <LandingHeader {...stack.header} {...stack?.$?.header} />
              )}
            </Container>
          </MuiThemeProvider>
          <ContentWrapper>
            {stack.page_options?.use_section_layout ? (
              <LandingContent {...stack} />
            ) : (
              <Container>
                <LegacyLandingContent theme={selectedTheme} {...stack} />
              </Container>
            )}
          </ContentWrapper>
          <Container>
            {stack.footer.background_image && (
              <PrerenderSafeLazyLoad>
                <FooterImg src={stack.footer.background_image.url} />
              </PrerenderSafeLazyLoad>
            )}
          </Container>
        </LandingWrapper>
        {stack.footer?.cta?.length > 0 && (
          <PrerenderSafeLazyLoad>
            <SimpleFooterCTA {...stack.footer?.cta?.[0]} />
          </PrerenderSafeLazyLoad>
        )}
      </MuiThemeProvider>
    </Fragment>
  );
};

export default connect(
  state => ({
    stack: state.landing.stack,
    loading: state.landing.loading
  }),
  { getLandingStack, setTopNavColorLight, setTopNavColorDark }
)(Landing);
